import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import {
  Building2, ArrowLeft, ArrowRightLeft, Loader2, CheckCircle2, AlertTriangle,
  Shield, Target, FileText, Bug, Wrench, ClipboardList, TrendingUp,
  User, Mail, Calendar, Play, Briefcase, Download
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { FW_NAMES, FW_COLORS } from "../constants/frameworks";

const ClientDashboard = () => {
  const { clientOrgId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/clients/${clientOrgId}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `client_report_${clientOrgId}_${new Date().toISOString().slice(0,10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(lang === "es" ? "PDF exportado" : "PDF exported");
    } catch {
      toast.error(lang === "es" ? "Error al exportar PDF" : "PDF export failed");
    } finally { setExporting(false); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${clientOrgId}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      toast.error(extractError(e, lang === "es" ? "Error al cargar" : "Failed to load"));
      navigate("/tenants");
    } finally { setLoading(false); }
  }, [clientOrgId, token, navigate, lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const switchToClient = async () => {
    setSwitching(true);
    try {
      await axios.put(`${API}/tenants/switch/${clientOrgId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "es" ? "Cambiando a tenant del cliente..." : "Switching to client tenant...");
      setTimeout(() => window.location.reload(), 800);
    } catch { toast.error("Failed"); setSwitching(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-[calc(100vh-4rem)]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!data) return null;

  const { client, health_score, engagements, latest_snapshots, tenant_metrics } = data;
  const isCurrent = client.organization_id === user?.organization_id;
  const healthColor = health_score >= 70 ? "text-green-500" : health_score >= 40 ? "text-yellow-500" : "text-red-500";
  const healthBg = health_score >= 70 ? "bg-green-500/10" : health_score >= 40 ? "bg-yellow-500/10" : "bg-red-500/10";
  const scoreColor = (pct) => pct >= 80 ? "text-green-500" : pct >= 60 ? "text-yellow-500" : pct >= 30 ? "text-orange-500" : "text-red-500";

  const typeIcons = { gap: Target, risk: AlertTriangle, soa: FileText };
  const typeColors = { gap: "#3B82F6", risk: "#EF4444", soa: "#22C55E" };
  const typeLabels = {
    gap: lang === "es" ? "Analisis de Brechas" : "Gap Analysis",
    risk: lang === "es" ? "Evaluacion de Riesgos" : "Risk Assessment",
    soa: lang === "es" ? "Declaracion de Aplicabilidad" : "Statement of Applicability"
  };

  return (
    <div className="p-6 space-y-6" data-testid="client-dashboard">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/tenants")} data-testid="back-to-tenants">
          <ArrowLeft className="w-4 h-4 mr-2" />{t("back")}
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-heading font-bold tracking-tight">{client.name}</h1>
              {isCurrent && <Badge variant="default">{lang === "es" ? "TENANT ACTIVO" : "ACTIVE TENANT"}</Badge>}
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              {client.domain || client.organization_id}
              {client.is_own_org && ` · ${lang === "es" ? "Tu organizacion" : "Your organization"}`}
            </p>
          </div>
        </div>
        {!isCurrent && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {lang === "es" ? "Exportar PDF" : "Export PDF"}
            </Button>
            <Button onClick={switchToClient} disabled={switching} data-testid="switch-to-client">
              {switching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
              {lang === "es" ? "Trabajar en este tenant" : "Work in this tenant"}
            </Button>
          </div>
        )}
        {isCurrent && (
          <Button variant="outline" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {lang === "es" ? "Exportar PDF" : "Export PDF"}
          </Button>
        )}
      </div>

      {/* KPIs Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`border-border ${healthBg}`} data-testid="kpi-health">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-background/50 flex items-center justify-center">
                <TrendingUp className={`w-5 h-5 ${healthColor}`} />
              </div>
              <div>
                <p className={`text-3xl font-heading font-bold ${healthColor}`}>{health_score}</p>
                <p className="text-xs text-muted-foreground">{lang === "es" ? "Salud" : "Health"}</p>
              </div>
            </div>
            <Progress value={health_score} className="h-1.5 mt-3" />
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-heading font-bold">{engagements.total}</p>
                <p className="text-xs text-muted-foreground">{lang === "es" ? "Proyectos" : "Projects"}</p>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              GAP: {engagements.gap.length} · Risk: {engagements.risk.length} · SoA: {engagements.soa.length}
            </p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-3xl font-heading font-bold text-red-500">{tenant_metrics.risks_critical}</p>
                <p className="text-xs text-muted-foreground">{t("critical")} {lang === "es" ? "Riesgos" : "Risks"}</p>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              {tenant_metrics.risks_total} {lang === "es" ? "totales" : "total"} · {tenant_metrics.risks_high} {t("high")}
            </p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Bug className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-3xl font-heading font-bold text-orange-500">{tenant_metrics.incidents_open}</p>
                <p className="text-xs text-muted-foreground">{lang === "es" ? "Incidentes" : "Incidents"}</p>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              {tenant_metrics.incidents_total} {lang === "es" ? "totales" : "total"} · {tenant_metrics.cas_open} CAs {lang === "es" ? "abiertas" : "open"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Assessments Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {["gap", "risk", "soa"].map(type => {
          const Icon = typeIcons[type];
          const snap = latest_snapshots[type];
          const items = engagements[type];
          return (
            <Card key={type} className="border-border" data-testid={`assessment-${type}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5" style={{ color: typeColors[type] }} />
                    <CardTitle className="text-base">{typeLabels[type]}</CardTitle>
                  </div>
                  <Badge variant="secondary">{items.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {!snap ? (
                  <p className="text-sm text-muted-foreground italic">{lang === "es" ? "Sin snapshots aun" : "No snapshots yet"}</p>
                ) : type === "gap" ? (
                  <>
                    <div className="text-center">
                      <span className={`text-4xl font-heading font-bold ${scoreColor(snap.coverage_percentage)}`}>{snap.coverage_percentage}%</span>
                      <p className="text-xs text-muted-foreground">{lang === "es" ? "Cobertura" : "Coverage"}</p>
                    </div>
                    <Progress value={snap.coverage_percentage} className="h-1.5" />
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" />{snap.implemented} {t("implemented")}</div>
                      <div className="flex items-center gap-1 text-red-500"><Target className="w-3 h-3" />{snap.gap_count} {t("gap.gaps_found")}</div>
                    </div>
                  </>
                ) : type === "risk" ? (
                  <>
                    <div className="text-center">
                      <span className="text-4xl font-heading font-bold">{snap.total_risks}</span>
                      <p className="text-xs text-muted-foreground">{lang === "es" ? "Riesgos totales" : "Total risks"}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className="text-red-500">{t("critical")}: {snap.by_level?.critical || 0}</div>
                      <div className="text-orange-500">{t("high")}: {snap.by_level?.high || 0}</div>
                      <div className="text-yellow-500">{t("medium")}: {snap.by_level?.medium || 0}</div>
                      <div className="text-green-500">{t("low")}: {snap.by_level?.low || 0}</div>
                    </div>
                    <p className="text-xs text-muted-foreground">{lang === "es" ? "Tratados" : "Treated"}: {snap.treatment_ratio}%</p>
                  </>
                ) : (
                  <>
                    <div className="text-center">
                      <span className={`text-4xl font-heading font-bold ${scoreColor(snap.coverage_percentage)}`}>{snap.coverage_percentage}%</span>
                      <p className="text-xs text-muted-foreground">{lang === "es" ? "Cobertura SoA" : "SoA Coverage"}</p>
                    </div>
                    <Progress value={snap.coverage_percentage} className="h-1.5" />
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className="text-green-500">{snap.implemented} {t("implemented")}</div>
                      <div className="text-yellow-500">{snap.in_progress} {t("in_progress")}</div>
                      <div className="text-muted-foreground">{snap.total_entries} {lang === "es" ? "entradas" : "entries"}</div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tenant Modules Row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { key: "soa_entries_total", icon: ClipboardList, label: lang === "es" ? "SoA Entries" : "SoA Entries", color: "text-green-500" },
          { key: "docs_total", icon: FileText, label: lang === "es" ? "Documentos" : "Documents", color: "text-blue-500" },
          { key: "evidence_total", icon: Shield, label: lang === "es" ? "Evidencias" : "Evidence", color: "text-purple-500" },
          { key: "audits_total", icon: CheckCircle2, label: lang === "es" ? "Auditorias" : "Audits", color: "text-teal-500" },
          { key: "cas_total", icon: Wrench, label: lang === "es" ? "Acciones Correctivas" : "Corrective Actions", color: "text-orange-500" },
          { key: "incidents_total", icon: Bug, label: lang === "es" ? "Incidentes" : "Incidents", color: "text-red-500" }
        ].map(m => {
          const Icon = m.icon;
          return (
            <Card key={m.key} className="border-border">
              <CardContent className="pt-3 pb-3 text-center">
                <Icon className={`w-5 h-5 mx-auto mb-1 ${m.color}`} />
                <p className="text-xl font-heading font-bold">{tenant_metrics[m.key] || 0}</p>
                <p className="text-[10px] text-muted-foreground">{m.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Projects Tabs */}
      <Tabs defaultValue="gap">
        <TabsList>
          <TabsTrigger value="gap" data-testid="tab-gap">GAP ({engagements.gap.length})</TabsTrigger>
          <TabsTrigger value="risk" data-testid="tab-risk">Risk ({engagements.risk.length})</TabsTrigger>
          <TabsTrigger value="soa" data-testid="tab-soa">SoA ({engagements.soa.length})</TabsTrigger>
        </TabsList>
        {["gap", "risk", "soa"].map(type => (
          <TabsContent key={type} value={type} className="mt-4">
            {engagements[type].length === 0 ? (
              <Card className="border-dashed border-2"><CardContent className="py-8 text-center text-sm text-muted-foreground">
                {lang === "es" ? "No hay proyectos de este tipo para este cliente." : "No projects of this type for this client."}
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {engagements[type].map(p => (
                  <motion.div key={p.engagement_id} whileHover={{ scale: 1.01 }}>
                    <Card className="border-border" data-testid={`proj-${p.engagement_id}`}>
                      <CardContent className="pt-4 space-y-2">
                        <div className="flex items-start justify-between">
                          <h4 className="font-semibold text-sm">{p.project_name}</h4>
                          <Badge variant="outline" className="text-[10px]">{t(`eng.status.${p.status}`)}</Badge>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: FW_COLORS[p.framework] }} />
                          <span className="text-[11px]">{FW_NAMES[p.framework]}</span>
                        </div>
                        {p.result_snapshot && (
                          <div className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                            {type === "gap" && <>Coverage: <span className={`font-bold ${scoreColor(p.result_snapshot.coverage_percentage)}`}>{p.result_snapshot.coverage_percentage}%</span></>}
                            {type === "risk" && <>{p.result_snapshot.total_risks} {lang === "es" ? "riesgos" : "risks"} · {p.result_snapshot.treatment_ratio}% {lang === "es" ? "tratados" : "treated"}</>}
                            {type === "soa" && <>Coverage: <span className={`font-bold ${scoreColor(p.result_snapshot.coverage_percentage)}`}>{p.result_snapshot.coverage_percentage}%</span> · {p.result_snapshot.total_entries} entries</>}
                          </div>
                        )}
                        <div className="text-[10px] text-muted-foreground space-y-0.5">
                          {p.contact_name && <div className="flex items-center gap-1"><User className="w-3 h-3" />{p.contact_name}</div>}
                          {p.contact_email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{p.contact_email}</div>}
                          {p.responsible && <div className="flex items-center gap-1"><User className="w-3 h-3" />{p.responsible}</div>}
                          <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(p.created_at).toLocaleDateString()}</div>
                        </div>
                        <Link to={type === "gap" ? "/gap-analysis" : type === "risk" ? "/risks" : "/soa"}>
                          <Button size="sm" variant="outline" className="w-full h-7 text-[11px]">
                            <Play className="w-3 h-3 mr-1" />{lang === "es" ? "Ir al modulo" : "Go to module"}
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default ClientDashboard;
